// MobileClientDetail — scheda cliente con tap-to-call, WhatsApp, edit veloce
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin, User, Edit3, Save, X, Calendar, Clock, FileText, ChevronDown, Loader2, Plus, ExternalLink, Trash2, AlertCircle, Mic, MessageSquareText } from "lucide-react";
import MobileShell from "@/components/mobile/MobileShell";
import { ReminderDialog } from "@/components/ReminderDialog";
import QuickMsgSheet from "@/components/QuickMsgSheet";
import VoiceJournal from "@/components/VoiceJournal";
import { api } from "@/lib/api";
import { AttachmentList } from "@/components/UpdateAttachments";
import { telLink, waLink, mapsLink, buzz } from "@/lib/mobile";
import { fmtDateTimeIT, fmtDateIT, todayISO, STATI_CLIENTE_BASE, getCustomStates, getHiddenStates } from "@/lib/format";
import { toast } from "sonner";

export default function MobileClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [newNote, setNewNote] = useState("");
  const [showRem, setShowRem] = useState(false);
  const [showMsg, setShowMsg] = useState(false);
  const [showAppt, setShowAppt] = useState(false);
  const [states, setStates] = useState([]);
  const [reminders, setReminders] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [cl, rs] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get("/reminders", { params: { clientId: id, limit: 50 } }),
      ]);
      setC(cl.data);
      setEdit({ stato: cl.data.stato || "", crif: cl.data.crif || "", data: cl.data.data || "", note: cl.data.note || "", citta: cl.data.citta || "", indirizzo: cl.data.indirizzo || "" });
      // Stati: usa localStorage (base + custom - hidden)
      const hidden = getHiddenStates();
      const custom = getCustomStates();
      const allStates = [...STATI_CLIENTE_BASE.filter((s) => !hidden.includes(s)), ...custom];
      setStates(allStates.map((name) => ({ name })));
      setReminders(rs.data.items || []);
    } catch (e) {
      toast.error("Errore caricamento cliente");
      nav("/m/clienti");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const saveEdit = async () => {
    try {
      await api.patch(`/clients/${id}`, edit);
      toast.success("Salvato");
      setEditing(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  const quickStato = async (stato) => {
    if (!stato || stato === c.stato) return;
    try {
      await api.patch(`/clients/${id}`, { stato });
      toast.success(`Stato: ${stato}`);
      buzz();
      load();
    } catch { toast.error("Errore"); }
  };

  const addNote = async () => {
    const text = (newNote || "").trim();
    if (!text) return;
    try {
      await api.post(`/clients/${id}/updates`, { text });
      setNewNote("");
      buzz();
      load();
    } catch { toast.error("Errore"); }
  };

  if (loading || !c) {
    return (
      <MobileShell title="Cliente" back>
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      </MobileShell>
    );
  }

  const fullName = `${c.cognome || ""} ${c.nome || ""}`.trim() || "(senza nome)";
  const isHot = ["INTERESSATO", "PROPORRE PP", "ATTESA DOCUMENTI"].includes(c.stato);
  const updates = (c.updates || []).slice().sort((a, b) => (b.when || "").localeCompare(a.when || ""));

  return (
    <MobileShell title={fullName} subtitle={c.stato || "Senza stato"} back
      action={
        editing ? (
          <button
            onClick={saveEdit}
            data-testid="mclient-save"
            className="font-semibold text-sm px-3 py-1.5 rounded-full"
            style={{ background: "#B7955B", color: "#fff", border: "1px solid #A48148" }}
          >
            Salva
          </button>
        ) : (
          <button
            onClick={() => setEditing(true)}
            data-testid="mclient-edit"
            className="flex items-center justify-center w-9 h-9 rounded-full active:scale-95 transition-transform"
            style={{ background: "rgba(183,149,91,0.18)", border: "1px solid rgba(183,149,91,0.4)", color: "#B7955B" }}
            aria-label="Modifica anagrafica"
          >
            <Edit3 className="w-4 h-4" strokeWidth={2} />
          </button>
        )
      }
    >
      {/* Hero card */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#172A45] text-white rounded-sm p-4 mb-3 -mt-2">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-serif font-bold shrink-0 ${isHot ? "bg-[#B7955B]" : "bg-white/15"}`}>
            {(c.cognome || c.nome || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg leading-tight truncate">{fullName}</div>
            {c.cf && <div className="text-[11px] text-white/60 font-mono">{c.cf}</div>}
            {c.fonte && <div className="text-[10px] uppercase tracking-wider text-[#B7955B] mt-0.5">{c.fonte}</div>}
          </div>
        </div>
        {/* Quick action buttons */}
        <div className={`grid ${c.telefono ? "grid-cols-4" : "grid-cols-3"} gap-2`}>
          {c.telefono ? (
            <>
              <a href={telLink(c.telefono)} onClick={() => buzz()}
                 className="bg-emerald-600 active:bg-emerald-700 text-white py-2.5 rounded-sm flex items-center justify-center gap-1.5 text-sm font-medium btn-press"
                 data-testid="mclient-call">
                <Phone className="w-4 h-4" /> Chiama
              </a>
              <a href={waLink(c.telefono)} target="_blank" rel="noreferrer" onClick={() => buzz()}
                 className="bg-[#25D366] active:bg-[#1faa55] text-white py-2.5 rounded-sm flex items-center justify-center gap-1.5 text-sm font-medium btn-press"
                 data-testid="mclient-wa">
                <MessageCircle className="w-4 h-4" /> WA
              </a>
              <button onClick={() => { buzz(); setShowMsg(true); }} data-testid="mclient-msg"
                      className="bg-sky-600 active:bg-sky-700 text-white py-2.5 rounded-sm flex items-center justify-center gap-1.5 text-sm font-medium btn-press">
                <MessageSquareText className="w-4 h-4" /> Msg
              </button>
            </>
          ) : (
            <div className="col-span-2 bg-white/10 text-white/60 py-2.5 rounded-sm text-center text-xs">Nessun numero</div>
          )}
          <button onClick={() => { setShowAppt(true); }} data-testid="mclient-appt"
                  className="bg-[#B7955B] active:bg-[#A18048] text-white py-2.5 rounded-sm flex items-center justify-center gap-1.5 text-sm font-medium btn-press">
            <Calendar className="w-4 h-4" /> Appunt.
          </button>
        </div>
        <QuickMsgSheet client={c} open={showMsg} onOpenChange={setShowMsg} />
      </div>

      {/* Quick edit stato (chips) */}
      {!editing && (
        <div className="bg-white p-2 mb-3" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
          <div className="text-[9px] font-bold uppercase tracking-[0.16em] mb-1.5 px-1" style={{ color: "#94A3B8" }}>Stato</div>
          <div className="flex flex-wrap gap-1">
            {(states.length > 0 ? states.map((s) => s.name) : ["NON RISP", "INTERESSATO", "ATTESA DOCUMENTI", "PROPORRE PP", "KO"]).map((s) => (
              <button key={s} onClick={() => quickStato(s)} data-testid={`mclient-stato-${s}`}
                      className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium ${c.stato === s ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-700 active:bg-slate-200"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex bg-white overflow-hidden mb-3" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
        {[
          { v: "info", label: "Info" },
          { v: "voice", label: "🎙 Voice" },
          { v: "timeline", label: `Timeline (${updates.length})` },
          { v: "reminders", label: `Prom. (${reminders.length})` },
        ].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`mclient-tab-${t.v}`}
                  className={`flex-1 py-2 text-xs font-medium transition ${tab === t.v ? "bg-[#0A192F] text-white" : "text-slate-600"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB CONTENT */}
      {tab === "info" && (
        <div className="space-y-2">
          {editing ? (
            <EditForm edit={edit} setEdit={setEdit} states={states} onCancel={() => { setEditing(false); setEdit({ stato: c.stato || "", crif: c.crif || "", data: c.data || "", note: c.note || "", citta: c.citta || "", indirizzo: c.indirizzo || "" }); }} onSave={saveEdit} />
          ) : (
            <>
              <InfoRow icon={Phone} label="Telefono" value={c.telefono} actionHref={telLink(c.telefono)} actionLabel="Chiama" />
              {c.telefono2 && <InfoRow icon={Phone} label="Telefono 2" value={c.telefono2} actionHref={telLink(c.telefono2)} actionLabel="Chiama" />}
              {c.mail && <InfoRow icon={Mail} label="Email" value={c.mail} actionHref={`mailto:${c.mail}`} actionLabel="Email" />}
              {(c.indirizzo || c.citta) && (
                <InfoRow icon={MapPin} label="Indirizzo" value={[c.indirizzo, c.citta].filter(Boolean).join(", ")}
                         actionHref={mapsLink([c.indirizzo, c.citta].filter(Boolean).join(", "))} actionLabel="Mappa" external />
              )}
              <InfoRow icon={Calendar} label="Recall" value={c.data || "—"} highlight={c.data === todayISO()} />
              <InfoRow icon={User} label="Stato CRIF" value={c.crif || "—"} />
              {c.istituto && <InfoRow icon={FileText} label="Istituto" value={c.istituto} />}
              {c.atc && <InfoRow icon={User} label="ATC" value={c.atc} />}
              {c.note && (
                <div className="bg-white p-3" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
                  <div className="text-[9px] font-bold uppercase tracking-[0.16em] mb-1" style={{ color: "#94A3B8" }}>Note</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.note}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "voice" && (
        <VoiceJournal clientId={c.id} onApplied={load} />
      )}

      {tab === "timeline" && (
        <div className="space-y-2">
          {/* New note input */}
          <div className="bg-white p-2" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
            <div className="flex gap-2">
              <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Aggiungi un aggiornamento..." rows={2} data-testid="mclient-newnote"
                        className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded-sm resize-y focus:outline-none focus:border-[#0A192F]" />
              <button onClick={addNote} disabled={!newNote.trim()} data-testid="mclient-addnote"
                      className="bg-[#0A192F] text-white rounded-sm px-3 disabled:opacity-40 self-stretch flex items-center justify-center">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          {updates.length === 0 ? (
            <div className="bg-white p-8 text-center text-slate-400 text-sm" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
              Nessun aggiornamento ancora.
            </div>
          ) : (
            updates.map((u, i) => {
              const kindColor = {
                campaign_assigned: "border-emerald-500",
                voice_note: "border-rose-500",
                crosssell_contact: "border-sky-500",
              }[u.kind] || "border-[#B7955B]";
              const author = u.by_name || (u.by ? "Utente" : null);
              return (
                <div key={i} className={`bg-white border-l-2 ${kindColor} border-y border-r border-slate-200 rounded-sm p-3`} data-testid={`mclient-upd-${i}`}>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-400 mb-1 flex-wrap">
                    <span className="tabular-nums">{fmtDateTimeIT(u.when)}</span>
                    {author && <span className="text-slate-600 font-medium">· <span >{author}</span></span>}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{u.text}</p>
                  <AttachmentList clientId={id} attachments={u.attachments} />
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === "reminders" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setShowRem(true)} data-testid="mclient-newrem"
                    className="bg-[#0A192F] text-white py-2.5 rounded-sm text-sm font-medium flex items-center justify-center gap-1.5 btn-press">
              <Clock className="w-4 h-4" /> Promemoria
            </button>
            <button onClick={() => setShowAppt(true)}
                    className="bg-[#B7955B] text-white py-2.5 rounded-sm text-sm font-medium flex items-center justify-center gap-1.5 btn-press">
              <Calendar className="w-4 h-4" /> Appuntamento
            </button>
          </div>
          {reminders.length === 0 ? (
            <div className="bg-white p-8 text-center text-slate-400 text-sm" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
              Nessun promemoria collegato.
            </div>
          ) : (
            reminders.map((r) => {
              const overdue = !r.completed && r.when && r.when.slice(0, 10) < todayISO();
              return (
                <div key={r.id} className={`bg-white border rounded-sm p-3 ${overdue ? "border-rose-300 bg-rose-50/30" : r.appointment ? "border-[#E5D7B6] bg-[#F5EFE3]/40" : "border-slate-200"}`}>
                  <div className="flex items-start gap-2">
                    {r.appointment ? <Calendar className="text-[#B7955B] mt-0.5 text-sky-600 w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${r.completed ? "line-through text-slate-400" : "text-slate-900"}`}>{r.title}</div>
                      <div className="text-[11px] text-slate-500 tabular-nums">{fmtDateTimeIT(r.when)}</div>
                      {overdue && <div className="text-[10px] text-rose-600 font-semibold uppercase">Scaduto</div>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <ReminderDialog open={showRem} onClose={() => setShowRem(false)} onSaved={() => { setShowRem(false); load(); }} appointment={false} clientId={id} clientName={fullName} />
      <ReminderDialog open={showAppt} onClose={() => setShowAppt(false)} onSaved={() => { setShowAppt(false); load(); }} appointment={true} clientId={id} clientName={fullName} />
    </MobileShell>
  );
}

function InfoRow({ icon: Icon, label, value, actionHref, actionLabel, external, highlight }) {
  if (!value || value === "—") return null;
  return (
    <div className={`bg-white border ${highlight ? "border-[#B7955B]" : "border-slate-200"} rounded-sm p-3 flex items-center gap-3`}>
      <Icon className={`w-4 h-4 shrink-0 ${highlight ? "text-[#B7955B]" : "text-slate-400"}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[9px] font-bold uppercase tracking-[0.16em]" style={{ color: "#94A3B8" }}>{label}</div>
        <div className="text-sm text-slate-900 truncate">{value}</div>
      </div>
      {actionHref && (
        <a href={actionHref} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}
           className="text-xs text-[#B7955B] font-medium px-2 py-1 active:bg-[#F5EFE3] rounded-sm flex items-center gap-1 mtap">
          {actionLabel} {external && <ExternalLink className="w-3 h-3" />}
        </a>
      )}
    </div>
  );
}

function EditForm({ edit, setEdit, states, onCancel, onSave }) {
  const upd = (k, v) => setEdit({ ...edit, [k]: v });
  return (
    <div className="space-y-2">
      <div className="bg-white p-3" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
        <Field label="Stato">
          <select value={edit.stato} onChange={(e) => upd("stato", e.target.value)}
                  className="w-full px-2 py-2 text-sm border border-slate-200 rounded-sm bg-white">
            <option value="">—</option>
            {states.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="CRIF">
          <select value={edit.crif} onChange={(e) => upd("crif", e.target.value)}
                  className="w-full px-2 py-2 text-sm border border-slate-200 rounded-sm bg-white">
            <option value="">—</option>
            <option value="OK">OK</option>
            <option value="NO">NO</option>
            <option value="DA VERIFICARE">DA VERIFICARE</option>
          </select>
        </Field>
        <Field label="Data recall">
          <input type="date" value={edit.data} onChange={(e) => upd("data", e.target.value)}
                 className="w-full px-2 py-2 text-sm border border-slate-200 rounded-sm tabular-nums" />
        </Field>
        <Field label="Città">
          <input value={edit.citta} onChange={(e) => upd("citta", e.target.value)}
                 className="w-full px-2 py-2 text-sm border border-slate-200 rounded-sm" />
        </Field>
        <Field label="Indirizzo">
          <input value={edit.indirizzo} onChange={(e) => upd("indirizzo", e.target.value)}
                 className="w-full px-2 py-2 text-sm border border-slate-200 rounded-sm" />
        </Field>
        <Field label="Note">
          <textarea rows={3} value={edit.note} onChange={(e) => upd("note", e.target.value)}
                    className="w-full px-2 py-2 text-sm border border-slate-200 rounded-sm resize-y" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={onCancel} className="bg-white border border-slate-200 text-slate-700 py-2.5 rounded-sm text-sm font-medium">Annulla</button>
        <button onClick={onSave} data-testid="mclient-savebtn" className="bg-[#0A192F] text-white py-2.5 rounded-sm text-sm font-medium flex items-center justify-center gap-1.5">
          <Save className="w-4 h-4" /> Salva
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-2.5 last:mb-0">
      <span className="text-[9px] font-bold uppercase tracking-[0.16em] block mb-1" style={{ color: "#94A3B8" }}>{label}</span>
      {children}
    </label>
  );
}
